<template>
  <div class="photo-list-wrapper">
    <h2>
      {{ $t("app.components.photolist.chronofox_screenshots") }}
    </h2>

    <div class="photo-list">
      <div v-for="(photo, index) in photos" :key="index" class="photo-item">
        <img
          :src="photo.image"
          :title="$t(photo.title)"
          :alt="$t(photo.alt)"
          class="photo-image"
          @click="expandImage(photo.image)"
        />
        <div class="photo-content">
          <h2>{{ $t(photo.title) }}</h2>
          <p>{{ $t(photo.description) }}</p>
        </div>
      </div>
    </div>

    <!-- Modal for full-screen image -->
    <transition name="fade">
      <div v-if="expandedImage" class="modal" @click="closeImage">
        <img
          :src="expandedImage"
          class="modal-image"
          alt="Expanded image"
          title="Expanded image"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
// Image imports
import pdfCsvReports from "@/assets/website/features/pdf-csv-reports.png";
import manageableActivities from "@/assets/website/features/manageable-activities.png";
import revenueDashboards from "@/assets/website/features/dashboard.png";
import manageableCustomers from "@/assets/website/features/manageable-customers.png";
import digitalClock from "@/assets/website/features/digital-clock.png";
import projects from "@/assets/website/features/projects.png";

const expandedImage = ref<string | null>(null);
const photos = [
  {
    image: pdfCsvReports,
    title: "app.components.photolist.pdfandcsvreports_title",
    alt: "app.components.photolist.pdfandcsvreports_alt",
    description: "app.components.photolist.pdfandcsvreports_description",
  },
  {
    image: manageableActivities,
    title: "app.components.photolist.manageableactivities_title",
    alt: "app.components.photolist.manageableactivities_alt",
    description: "app.components.photolist.manageableactivities_description",
  },
  {
    image: revenueDashboards,
    title: "app.components.photolist.revenuedashboards_title",
    alt: "app.components.photolist.revenuedashboards_alt",
    description: "app.components.photolist.revenuedashboards_description",
  },
  {
    image: manageableCustomers,
    title: "app.components.photolist.manageablecustomers_title",
    alt: "app.components.photolist.manageablecustomers_alt",
    description: "app.components.photolist.manageablecustomers_description",
  },
  {
    image: digitalClock,
    title: "app.components.photolist.digitalclock_title",
    alt: "app.components.photolist.digitalclock_alt",
    description: "app.components.photolist.digitalclock_description",
  },
  {
    image: projects,
    title: "app.components.photolist.projects_title",
    alt: "app.components.photolist.projects_alt",
    description: "app.components.photolist.projects_description",
  },
];

const expandImage = (image: string) => {
  expandedImage.value = image;
};

const closeImage = () => {
  expandedImage.value = null;
};
</script>

<style scoped>
.photo-list-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
}

.photo-list-wrapper > h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 3rem;
  text-align: center;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.photo-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 80px;
}

.photo-item {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  padding: 40px;
  background: white;
  border-radius: 15px;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.photo-item:nth-child(even) {
  grid-template-columns: 1fr 1fr;
}

.photo-item:nth-child(even) .photo-image {
  order: 2;
}

.photo-item:nth-child(even) .photo-content {
  order: 1;
}

.photo-item:hover {
  transform: translateY(-5px);
  border-color: #ff6b35;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.photo-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.photo-image:hover {
  transform: scale(1.02);
  box-shadow: 0 15px 40px rgba(255, 107, 53, 0.2);
}

.photo-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.photo-content h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.photo-content p {
  font-size: 1.05rem;
  color: #4b5563;
  line-height: 1.8;
  margin: 0;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  backdrop-filter: blur(5px);
}

.modal-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 15px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5);
}

/* Fade animation for modal */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .photo-item,
  .photo-item:nth-child(even) {
    grid-template-columns: 1fr;
  }

  .photo-item:nth-child(even) .photo-image,
  .photo-item:nth-child(even) .photo-content {
    order: 0;
  }

  .photo-content {
    text-align: center;
    align-items: center;
  }
}

@media (max-width: 768px) {
  .photo-list-wrapper {
    padding: 40px 20px;
  }

  .photo-list-wrapper > h2 {
    font-size: 2rem;
  }

  .photo-item {
    padding: 30px 20px;
    gap: 25px;
  }

  .photo-content h2 {
    font-size: 1.5rem;
  }

  .photo-content p {
    font-size: 1rem;
  }

  .modal-image {
    max-width: 95%;
    max-height: 95%;
  }
}
</style>
